/**
 * A reading order drawn as a route: a numbered medallion per stop, a dotted
 * line from each to the next, and no line after the last. The one new idea in
 * the design, so it is written once and used twice — the whole path on a
 * series page, and three stops of it on a book's page.
 *
 * It is still an ordered list underneath. The medallion's number is hidden
 * from assistive technology because the list already says where each item
 * falls; a screen reader hears „1. Стража! Стража!“, not the number twice.
 */

import { Link } from 'react-router'

export interface PathStop {
  /** Where the stop falls in the whole path, from 1. */
  position: number
  title: string
  href: string
  year: number | null
  note?: string | null
  /** Read by the signed-in reader: the medallion is filled (the account page's progress). */
  done?: boolean
}

export function Path({
  stops,
  label,
  current,
  compact = false,
  doneLabel = '',
}: {
  stops: PathStop[]
  label: string
  /** The position to mark as "you are here", on a book's page. */
  current?: number
  compact?: boolean
  /** Said to a screen reader before a stop marked `done`, which it cannot see is filled. */
  doneLabel?: string
}) {
  return (
    <ol className={compact ? 'path path--compact' : 'path'} aria-label={label}>
      {stops.map((stop, index) => {
        const isCurrent = stop.position === current
        const classes = ['path__stop']
        if (stop.position === 1) classes.push('path__stop--start')
        if (isCurrent) classes.push('path__stop--current')
        if (stop.done === true) classes.push('path__stop--done')
        return (
          // `value` keeps the list's own numbering right when it starts part-way, as on a book's page.
          <li key={stop.href} className={classes.join(' ')} value={stop.position}>
            <span className="path__medallion" data-path="medallion" aria-hidden="true">
              {stop.position}
            </span>
            {stop.done === true ? <span className="visually-hidden">{doneLabel} </span> : null}
            {index < stops.length - 1 ? <span className="path__line" data-path="line" aria-hidden="true" /> : null}
            <div className="path__body">
              <span className="path__title">
                {isCurrent ? (
                  <span aria-current="true">{stop.title}</span>
                ) : (
                  <Link to={stop.href}>{stop.title}</Link>
                )}
                {stop.year === null ? null : <span className="path__year">{stop.year}</span>}
              </span>
              {stop.note === undefined || stop.note === null ? null : <p className="path__note">{stop.note}</p>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
